import { useEffect, useState } from "react";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.js";

interface Grant { id: string; capability: string; scope_id?: string; influencer_id?: string; expires_at?: string; status: string; }
interface Decision { id: string; status: string; recommendation?: string; reason?: string; created_at: string; }

export function AutonomyPage() {
  const { role } = useAuth();
  const [grants, setGrants] = useState<Grant[]>([]);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<{ grants: Grant[] }>("/capability-grants?limit=20").catch(() => ({ grants: [] })),
      api.get<{ decisions: Decision[] }>("/supervisor/decisions?limit=10").catch(() => ({ decisions: [] })),
    ]).then(([g, d]) => {
      setGrants(g.grants ?? []);
      setDecisions(d.decisions ?? []);
    }).finally(() => setLoading(false));
  }, []);

  async function handleRevoke(id: string) {
    if (!confirm("¿Revocar este permiso?")) return;
    try {
      await api.post(`/capability-grants/${id}/revoke`);
      setGrants(prev => prev.filter(g => g.id !== id));
    } catch { /* ignore */ }
  }

  if (loading) return <div style={{ padding: 24, color: "#888" }}>Cargando…</div>;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Autonomía y control</h1>
        <p style={{ color: "#666", fontSize: 13, margin: "4px 0 0" }}>Permisos de capacidad, supervisor y límites</p>
      </div>

      <div style={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 16px", marginBottom: 20, fontSize: 13, color: "#92400e" }}>
        El supervisor opera en modo advisory: propone pero nunca actúa sin aprobación del propietario. globalCapExpanded=false es un invariante estructural que no puede modificarse desde la UI ni la API.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, flexWrap: "wrap" }}>
        {/* Grants */}
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Permisos de capacidad</h2>
          {grants.length === 0 ? (
            <div style={{ background: "#fff", borderRadius: 10, padding: 24, color: "#888", fontSize: 13 }}>
              Sin permisos activos. Los permisos de capacidad los crea el propietario para habilitar acciones autónomas específicas.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {grants.map(g => (
                <div key={g.id} style={{ background: "#fff", borderRadius: 8, padding: "12px 16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{g.capability}</div>
                      <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                        {g.expires_at ? `Expira: ${new Date(g.expires_at).toLocaleDateString("es")}` : "Sin expiración"}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: g.status === "active" ? "#d8f3dc" : "#fef2f2", color: g.status === "active" ? "#2d6a4f" : "#dc2626", fontWeight: 600 }}>{g.status}</span>
                      {role === "owner" && g.status === "active" && (
                        <button onClick={() => handleRevoke(g.id)} style={{ padding: "3px 8px", border: "1px solid #fecaca", borderRadius: 4, background: "none", color: "#dc2626", cursor: "pointer", fontSize: 11 }}>Revocar</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Supervisor decisions */}
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Decisiones del supervisor</h2>
          {decisions.length === 0 ? (
            <div style={{ background: "#fff", borderRadius: 10, padding: 24, color: "#888", fontSize: 13 }}>
              Sin decisiones del supervisor. Las propuestas aparecen aquí para revisión del propietario antes de tomar efecto.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {decisions.map(d => (
                <div key={d.id} style={{ background: "#fff", borderRadius: 8, padding: "12px 16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a1a", marginBottom: 2 }}>{d.recommendation || "Propuesta"}</div>
                      {d.reason && <div style={{ fontSize: 11, color: "#666" }}>{d.reason}</div>}
                      <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{new Date(d.created_at).toLocaleDateString("es")}</div>
                    </div>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: "#f3f4f6", color: "#555", fontWeight: 600 }}>{d.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 24, background: "#fff", borderRadius: 10, padding: 20 }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 600 }}>Invariantes de seguridad (no modificables)</h3>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: "#666", lineHeight: 2 }}>
          <li>globalCapExpanded = false (el supervisor no puede ampliar el cap global)</li>
          <li>OUTBOUND_ACTIONS_ENABLED pausa global de acciones externas</li>
          <li>Edad ficticia ≥ 25 en personajes</li>
          <li>Ledger de comisiones append-only (sin UPDATE ni DELETE)</li>
          <li>Rasgos de identidad inmutables tras aprobación</li>
          <li>Credenciales nunca expuestas en respuestas de API</li>
          <li>Feedback permanente requiere aprobación del propietario</li>
        </ul>
      </div>
    </div>
  );
}
